import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { HelpCircle, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button as UiButton } from '@/components/ui/button';

type ConfirmModalProps = {
	show: boolean;
	innerText: string | 'Bạn chắc chắn thực hiện hành động này?';
	btnConfirmText?: string;
	btnCloseText?: string;
	onConfirmed(): void;
	onClose(): void;
	type: 'danger' | 'primary' | 'success';
	sizeClass?: 'sm' | 'md' | 'lg';
	isLoading?: boolean | false;
	isDisable?: boolean | false;
};

const typeToColor = (type: string) => {
    switch (type) {
        case 'danger': return 'text-destructive bg-destructive/10';
        case 'success': return 'text-emerald-500 bg-emerald-500/10';
        default: return 'text-primary bg-primary/10';
    }
};

const titleByType = (type: ConfirmModalProps['type']) => {
    switch (type) {
        case 'danger': return 'Xác nhận hành động quan trọng';
        case 'success': return 'Xác nhận hoàn tất';
        default: return 'Xác nhận thao tác';
    }
};

const iconByType = (type: ConfirmModalProps['type']) => {
    switch (type) {
        case 'danger': return <ShieldAlert className='h-8 w-8' />;
        case 'success': return <CheckCircle2 className='h-8 w-8' />;
        default: return <HelpCircle className='h-8 w-8' />;
    }
};

const sizeToClass = (sizeClass?: string) => {
    switch (sizeClass) {
        case 'sm': return 'max-w-sm';
        case 'md': return 'max-w-md';
        case 'lg': return 'max-w-lg';
        default: return 'max-w-sm';
    }
};

export const ConfirmModal = (props: ConfirmModalProps) => {
	const { show, innerText, onConfirmed, onClose, type, btnCloseText, btnConfirmText, isLoading, sizeClass, isDisable } = props;

	return (
        <AlertDialog open={show} onOpenChange={(open) => !open && onClose()}>
            <AlertDialogContent className={cn(sizeToClass(sizeClass))}>
                <AlertDialogHeader className="flex flex-col items-center justify-center space-y-4 sm:text-center">
                    <div className={cn("flex h-16 w-16 items-center justify-center rounded-full", typeToColor(type))}>
                        {iconByType(type)}
                    </div>
                    <AlertDialogTitle className="text-lg font-semibold sm:text-xl">{titleByType(type)}</AlertDialogTitle>
                    <AlertDialogDescription>
                        <div className='text-sm text-muted-foreground' dangerouslySetInnerHTML={{ __html: innerText || '' }}></div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-6 gap-2 sm:justify-center [&>*]:w-full sm:[&>*]:w-auto">
                    <AlertDialogCancel 
                        disabled={isLoading} 
                        onClick={onClose}
                        className="mt-0"
                    >
                        {btnCloseText || 'Đóng'}
                    </AlertDialogCancel>
                    <UiButton
                        type='button'
                        variant={type === 'danger' ? 'destructive' : 'default'}
                        disabled={isLoading || isDisable}
                        onClick={onConfirmed}
                    >
                        {isLoading ? 'Đang xử lý...' : (btnConfirmText || 'Xác nhận')}
                    </UiButton>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
	);
};
